import LocationIcon from '../../assets/icons/LocationIcon';
import classnames from 'classnames';
import useTheme from '../../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import CustomButton from '../buttons/CustomButton';
import { useMemo, useState } from 'react';
import CompanyDetailsModal from '../../modals/CompanyDetailsModal';
import InfoIcon from '../../assets/logo/InfoIcon';
import { CompanyDistanceInterface } from '../../pages/HomeScreen';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import HelperMethods from '../../services/exports/HelperMethods';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useRouter from '../../hooks/navigation/useRouter';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import { ButtonTypes } from '../../services/exports/Interfaces';

interface Props {
  data: CompanyDistanceInterface;
  className?: string;
}

export default function CompanyCard(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Franchise:CompanyCard' });

  const { data, className } = props;
  const { company, distance } = data;

  const { navigate } = useRouter();
  const theme = useTheme();
  const { saveMenu } = useAppMethods();
  const { is_open, details_text } = useOpeningStatus({ data: company });
  const { has_pre_order } = company;

  const [showInfoModal, setShowInfoModal] = useState(false);

  const toggleInfoModal = () => setShowInfoModal((prev) => !prev);

  const renderStatus = () => {
    return (
      <h6
        className={classnames('text-sm', {
          'text-primary': is_open,
          'text-danger': !is_open,
        })}
      >
        {details_text}
      </h6>
    );
  };

  const renderCompanyInfoModal = useMemo(
    () =>
      showInfoModal && <CompanyDetailsModal data={company} openModal={showInfoModal} toggleModal={toggleInfoModal} />,
    [company, showInfoModal],
  );

  return (
    <div className={classnames('w-full shadow rounded-lg p-md', className)}>
      <div className="flex justify-between">
        <h6 className="my-auto">{company.name}</h6>
        <div onClick={toggleInfoModal} className="h-8 w-8 cursor-pointer">
          <InfoIcon className="h-5 w-5 my-auto" color={theme.color.brand.primary} fill />
        </div>
      </div>
      {distance ? (
        <p className="my-xs text-xs text-gray-700">
          {t('distance', { distance: HelperMethods.formatDistance(distance) })}
        </p>
      ) : null}
      {company.location_name ? <h6 className="text-sm text-gray-700">{company.location_name}</h6> : null}
      <div className="flex mt-xs">
        <LocationIcon className="h-4 w-4 my-auto mr-2xs" color={theme.color.gray['300']} />
        <p className="text-sm text-gray-700">
          {AddressHelpers.setCountry(company?.country).format({
            street_name: company?.address,
            city: company?.city,
            zip_code: company?.zip_code,
          })}
        </p>
      </div>
      <div className="flex mt-xs">{renderStatus()}</div>
      {(is_open || has_pre_order) && (
        <CustomButton
          className="mt-md"
          titleClass="text-sm"
          title={t(is_open ? 'buttons.order_here' : 'buttons.pre_order')}
          buttonType={is_open ? ButtonTypes.primary : ButtonTypes.forth}
          onClick={() => {
            new Promise((resolve) => {
              saveMenu({ menuLoaded: false });
              resolve(true);
            }).then(() =>
              navigate({
                web: {
                  to: company.slug,
                },
              }),
            );
          }}
        />
      )}
      {renderCompanyInfoModal}
    </div>
  );
}
